import React, { useEffect, useState, useCallback } from 'react';
import './NotificationBanner.css';

/**
 * NotificationBanner - Displays real-time broadcast notifications as toasts
 */
function NotificationBanner({ notification, onDismiss }) {
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            setIsVisible(false);
            if (onDismiss) {
                onDismiss(notification?.id);
            }
        }, 300); // Match CSS animation duration
    }, [onDismiss, notification?.id]);

    useEffect(() => {
        if (notification) {
            setIsVisible(true);
            setIsExiting(false);

            // Auto-dismiss after 8 seconds
            const timer = setTimeout(() => {
                handleDismiss();
            }, 8000);

            return () => clearTimeout(timer);
        }
    }, [notification, handleDismiss]);

    if (!notification || !isVisible) {
        return null;
    }

    return (
        <div className={`notification-banner ${isExiting ? 'exiting' : ''}`}>
            <div className="notification-icon">
                {notification.type === 'broadcast' ? '📢' : '🔔'}
            </div>
            <div className="notification-content">
                <h4 className="notification-title">{notification.title}</h4>
                <p className="notification-body">{notification.body}</p>
            </div>
            <button
                className="notification-close"
                onClick={handleDismiss}
                aria-label="Dismiss notification"
            >
                ×
            </button>
        </div>
    );
}

/**
 * NotificationContainer - Manages multiple toast notifications
 */
export function NotificationContainer({ notifications, onClear }) {
    return (
        <div className="notification-container">
            {notifications.slice(0, 3).map((notification) => (
                <NotificationBanner
                    key={notification.id}
                    notification={notification}
                    onDismiss={onClear}
                />
            ))}
        </div>
    );
}

export default NotificationBanner;
